/** Low-wind hull split: TWS below this (kph) uses BSP threshold; at/above includes all grade-3 samples. */
export const LIVE_TABLE_LOW_WIND_TWS_KPH_LT = 20;

/** Low-wind foiling vs displacement split on boat speed (kph). */
export const LIVE_TABLE_FOILING_BSP_KPH_GE = 28;

export type FocusPosMode = 'upwind' | 'downwind' | 'reaching';

const META_KEYS = new Set(['timestamp', 'Datetime', 'datetime', 'source_name', 'source_id']);

function normChannelKey(s: string): string {
  return s.toLowerCase().replace(/_/g, '');
}

/** Read a numeric channel from a merged focus point (case / alias tolerant). */
export function readNumericFromPoint(pt: Record<string, unknown>, channel: string): number | null {
  const direct = pt[channel];
  if (direct !== null && direct !== undefined) {
    const num = typeof direct === 'number' && Number.isFinite(direct) ? direct : Number(direct);
    return Number.isFinite(num) ? num : null;
  }
  const n = normChannelKey(channel);
  for (const k of Object.keys(pt)) {
    if (META_KEYS.has(k)) continue;
    if (normChannelKey(k) !== n) continue;
    if (channel === 'GRADE' && k === 'Grade') continue;
    const raw = pt[k];
    if (raw === null || raw === undefined) return null;
    const num = typeof raw === 'number' && Number.isFinite(raw) ? raw : Number(raw);
    return Number.isFinite(num) ? num : null;
  }
  return null;
}

/** Realtime processor GRADE === 3 (high-quality samples). Uses key GRADE only — not maneuver "Grade". */
export function pointMatchesGrade3(pt: Record<string, unknown>): boolean {
  const raw = pt['GRADE'];
  if (raw === null || raw === undefined) return false;
  const num = typeof raw === 'number' && Number.isFinite(raw) ? raw : Number(raw);
  if (!Number.isFinite(num)) return false;
  return Math.round(num) === 3;
}

/** Realtime processor GRADE > 1 (grade 2 or 3). Uses key GRADE only — not maneuver "Grade". */
export function pointMatchesGradeAbove1(pt: Record<string, unknown>): boolean {
  const raw = pt['GRADE'];
  if (raw === null || raw === undefined) return false;
  const num = typeof raw === 'number' && Number.isFinite(raw) ? raw : Number(raw);
  if (!Number.isFinite(num)) return false;
  return Math.round(num) > 1;
}

/**
 * Low-wind displacement sample: GRADE > 1, TWS < 20 kph, BSP < 28 kph.
 * Used to decide whether the foiling/displacement toggle should be shown.
 */
export function pointIsLowWindDisplacementSample(pt: Record<string, unknown>): boolean {
  if (!pointMatchesGradeAbove1(pt)) return false;
  const tws = readNumericFromPoint(pt, 'Tws_kph');
  if (tws === null || tws >= LIVE_TABLE_LOW_WIND_TWS_KPH_LT) return false;
  const bsp = readNumericFromPoint(pt, 'Bsp_kph');
  if (bsp === null) return false;
  return bsp < LIVE_TABLE_FOILING_BSP_KPH_GE;
}

/** True when any merged focus point qualifies as low-wind displacement (GRADE > 1). */
export function focusPointsHaveDisplacementData(
  pointsBySource: Iterable<Iterable<Record<string, unknown>>>
): boolean {
  for (const points of pointsBySource) {
    for (const pt of points) {
      if (pointIsLowWindDisplacementSample(pt)) return true;
    }
  }
  return false;
}

/** TWA bands: Upwind 25–75°, Reaching 80–110°, Downwind 125–160° (abs TWA). */
export function pointMatchesTwaMode(pt: Record<string, unknown>, mode: FocusPosMode): boolean {
  const raw = pt['Twa_deg'] ?? pt['twa_deg'] ?? pt['Twa_n_deg'] ?? pt['twa_n_deg'];
  if (raw === null || raw === undefined) return false;
  const twa = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(twa)) return false;
  const absTwa = Math.abs(twa);
  switch (mode) {
    case 'upwind':
      return absTwa > 25 && absTwa < 75;
    case 'reaching':
      // Reaching band: |TWA| between 80 and 110 (inclusive).
      return absTwa >= 80 && absTwa <= 110;
    case 'downwind':
      return absTwa > 125 && absTwa < 160;
    default:
      return true;
  }
}

/**
 * Low-wind foiling filter on grade-3 samples.
 * TWS >= 20 kph: included regardless. TWS < 20: foilingEnabled => BSP >= 28, else BSP < 28.
 * Missing TWS or BSP when needed for low-wind split: excluded.
 */
export function pointMatchesFoilingFilter(pt: Record<string, unknown>, foilingEnabled: boolean): boolean {
  const tws = readNumericFromPoint(pt, 'Tws_kph');
  if (tws === null) return false;

  if (tws >= LIVE_TABLE_LOW_WIND_TWS_KPH_LT) return true;

  const bsp = readNumericFromPoint(pt, 'Bsp_kph');
  if (bsp === null) return false;

  if (foilingEnabled) return bsp >= LIVE_TABLE_FOILING_BSP_KPH_GE;
  return bsp < LIVE_TABLE_FOILING_BSP_KPH_GE;
}

export const FOCUS_RACE_ALL = 'ALL' as const;

/**
 * Race filter, |TWA| band, mandatory GRADE === 3, low-wind foiling filter.
 */
export function filterFocusPoints<T extends Record<string, unknown>>(
  points: T[],
  mode: FocusPosMode,
  foilingEnabled: boolean,
  raceKey: string,
  getRaceNumberKey: (rec: Record<string, unknown>) => string | null
): T[] {
  return points.filter((pt) => {
    const rec = pt as Record<string, unknown>;
    if (raceKey !== FOCUS_RACE_ALL) {
      const rk = getRaceNumberKey(rec);
      if (rk !== raceKey) return false;
    }
    if (!pointMatchesTwaMode(rec, mode)) return false;
    if (!pointMatchesGrade3(rec)) return false;
    if (!pointMatchesFoilingFilter(rec, foilingEnabled)) return false;
    return true;
  });
}

export function focusFoilingFilterLabel(foilingEnabled: boolean): string {
  return foilingEnabled ? 'Foiling' : 'BSP < 28';
}
